// Reportes de soporte (D95): periodo con su periodo anterior, resumen de casos (por area, persona, prioridad y
// origen), horas del equipo y bolsas de soporte. Todo puro, para probarlo y para armar el Excel con lo mismo que se ve.
import type { Caso } from '../api/casos';
import { esAdicional, HORA_ADICIONAL, PLANES, type Bolsa, type Movimiento, type Saldo } from '../api/bolsas';
import type { RegistroHoras } from '../api/proyectos';

export const SLA_MIN = 30;
const DIA = 86_400_000;

/* ---------------- periodo ---------------- */

export type PeriodoR = 'semana' | 'mes' | 'mesPasado' | 'trimestre';
export const PERIODOS_R: [PeriodoR, string][] = [
  ['semana', 'Últimos 7 días'],
  ['mes', 'Este mes'],
  ['mesPasado', 'Mes pasado'],
  ['trimestre', '3 meses'],
];
/** [desde, hasta) del periodo y [antes, finAntes) del periodo anterior con el que se compara. */
export function rangoR(p: PeriodoR, hoy = new Date()): { desde: Date; hasta: Date; antes: Date; finAntes: Date } {
  const manana = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + 1);
  const mes = (k: number) => new Date(hoy.getFullYear(), hoy.getMonth() + k, 1);
  if (p === 'semana') return { desde: new Date(manana.getTime() - 7 * DIA), hasta: manana, antes: new Date(manana.getTime() - 14 * DIA), finAntes: new Date(manana.getTime() - 7 * DIA) };
  // Este mes se compara con el mismo tramo del mes pasado (1 al mismo dia).
  if (p === 'mes') return { desde: mes(0), hasta: manana, antes: mes(-1), finAntes: new Date(Math.min(mes(0).getTime(), new Date(hoy.getFullYear(), hoy.getMonth() - 1, hoy.getDate() + 1).getTime())) };
  if (p === 'mesPasado') return { desde: mes(-1), hasta: mes(0), antes: mes(-2), finAntes: mes(-1) };
  return { desde: mes(-2), hasta: manana, antes: mes(-5), finAntes: mes(-2) };
}

/** Lunes a viernes entre dos fechas (para la ocupacion del equipo). */
export function diasHabiles(desde: Date, hasta: Date) {
  let n = 0;
  for (let t = new Date(desde.getFullYear(), desde.getMonth(), desde.getDate()); t < hasta; t = new Date(t.getFullYear(), t.getMonth(), t.getDate() + 1)) if (t.getDay() !== 0 && t.getDay() !== 6) n++;
  return n;
}

/* ---------------- ayudas ---------------- */

export const mediana = (xs: number[]) => percentil(xs, 50);
export function percentil(xs: number[], p: number) {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  if (p === 50) {
    const m = Math.floor(s.length / 2);
    return Math.round(s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2);
  }
  return Math.round(s[Math.min(s.length - 1, Math.ceil((p / 100) * s.length) - 1)]);
}
export const contar = <K>(xs: K[]) => {
  const m = new Map<K, number>();
  for (const x of xs) m.set(x, (m.get(x) ?? 0) + 1);
  return [...m].sort((a, b) => b[1] - a[1]);
};
const sumarMap = <K>(xs: [K, number][]) => {
  const m = new Map<K, number>();
  for (const [k, v] of xs) m.set(k, (m.get(k) ?? 0) + v);
  return [...m].sort((a, b) => b[1] - a[1]);
};
const cerrado = (c: Pick<Caso, 'estado'>) => c.estado === 'RESUELTO' || c.estado === 'CERRADO';
const activo = (c: Pick<Caso, 'estado'>) => c.estado === 'NUEVO' || c.estado === 'ABIERTO';
export const areaDe = (c: Pick<Caso, 'grupo'>) => c.grupo.trim() || 'Sin área';
/** Minutos hasta la primera respuesta (sin los casos traidos de Zammad), o null. */
export const minutosRespuesta = (c: Pick<Caso, 'primeraRespuesta' | 'creado' | 'origen'>) =>
  c.primeraRespuesta && c.origen !== 'ZAMMAD' ? Math.max(0, Math.round((Date.parse(c.primeraRespuesta) - Date.parse(c.creado)) / 60_000)) : null;
/** Minutos desde que entro hasta que se resolvio (sin Zammad), o null. */
export const minutosResolucion = (c: Pick<Caso, 'estado' | 'cerradoEn' | 'creado' | 'origen'>) =>
  cerrado(c) && c.cerradoEn && c.origen !== 'ZAMMAD' ? Math.max(0, Math.round((Date.parse(c.cerradoEn) - Date.parse(c.creado)) / 60_000)) : null;

/* ---------------- casos ---------------- */

export type FilaPersona = { id: string | null; asignados: number; resueltos: number; abiertos: number; dentroSla: number | null; horas: number };
export type FilaPrioridad = { prioridad: string; casos: number; respuestaMediana: number | null; dentroSla: number | null };
export type DetalleCasos = {
  entraron: number;
  resueltos: number;
  abiertos: number;
  pendientes: number;
  conSeguimiento: number;
  respuestaMediana: number | null;
  dentroSla: number | null;
  resolucionMediana: number | null;
  resolucionP90: number | null;
  horas: number;
  llegaronPorDia: number[];
  resueltosPorDia: number[];
  porPersona: FilaPersona[];
  porEmpresa: [string, number][];
  porOrigen: [string, number][];
  porPrioridad: FilaPrioridad[];
  porArea: [string, number][];
};

const pct = (xs: number[]) => (xs.length ? Math.round((100 * xs.filter((t) => t <= SLA_MIN).length) / xs.length) : null);
const PRIORIDADES = ['URGENTE', 'ALTA', 'NORMAL', 'BAJA'];

/**
 * Resumen de casos del periodo. Entraron = creados en el periodo; resueltos = cerrados en el periodo; abiertos y
 * pendientes = como estan hoy; respuesta y SLA sobre los que entraron; resolucion sobre los resueltos.
 * Las horas son las registradas en el periodo en esos casos.
 */
export function detalleCasos(lista: Caso[], horas: RegistroHoras[], desde: Date, hasta: Date): DetalleCasos {
  const en = (iso: string | null) => !!iso && Date.parse(iso) >= desde.getTime() && Date.parse(iso) < hasta.getTime();
  const nuevos = lista.filter((c) => en(c.creado));
  const resueltos = lista.filter((c) => cerrado(c) && en(c.cerradoEn));
  const tiempos = nuevos.map(minutosRespuesta).filter((x): x is number => x != null);
  const resol = resueltos.map(minutosResolucion).filter((x): x is number => x != null);
  const dias = Math.max(1, Math.round((hasta.getTime() - desde.getTime()) / DIA));
  const porDia = (xs: Caso[], fecha: (c: Caso) => string | null) => {
    const d = Array.from({ length: dias }, () => 0);
    for (const c of xs) d[Math.min(dias - 1, Math.max(0, Math.floor((Date.parse(fecha(c)!) - desde.getTime()) / DIA)))] += 1;
    return d;
  };
  const ids = new Set(lista.map((c) => c.id));
  const hs = horas.filter((h) => h.casoId && ids.has(h.casoId) && en(h.fecha));
  const horasDe = new Map(sumarMap(hs.map((h): [string | null, number] => [h.miembroId, h.minutos])));
  const personas = new Set<string | null>([...nuevos.map((c) => c.asignadoId), ...resueltos.map((c) => c.asignadoId), ...lista.filter(activo).map((c) => c.asignadoId), ...horasDe.keys()]);
  const porPersona = [...personas]
    .map((id): FilaPersona => {
      const suyos = nuevos.filter((c) => c.asignadoId === id);
      return {
        id,
        asignados: suyos.length,
        resueltos: resueltos.filter((c) => c.asignadoId === id).length,
        abiertos: lista.filter((c) => activo(c) && c.asignadoId === id).length,
        dentroSla: pct(suyos.map(minutosRespuesta).filter((x): x is number => x != null)),
        horas: horasDe.get(id) ?? 0,
      };
    })
    .sort((a, b) => b.asignados + b.resueltos - (a.asignados + a.resueltos));
  const prios = [...new Set([...PRIORIDADES, ...nuevos.map((c) => c.prioridad)])];
  return {
    entraron: nuevos.length,
    resueltos: resueltos.length,
    abiertos: lista.filter(activo).length,
    pendientes: lista.filter((c) => c.estado === 'PENDIENTE').length,
    conSeguimiento: lista.filter((c) => !cerrado(c) && !!c.seguimientoEn).length,
    respuestaMediana: mediana(tiempos),
    dentroSla: pct(tiempos),
    resolucionMediana: mediana(resol),
    resolucionP90: percentil(resol, 90),
    horas: hs.reduce((s, h) => s + h.minutos, 0),
    llegaronPorDia: porDia(nuevos, (c) => c.creado),
    resueltosPorDia: porDia(resueltos, (c) => c.cerradoEn),
    porPersona,
    porEmpresa: contar(nuevos.map((c) => c.empresa?.nombre ?? 'Sin empresa')),
    porOrigen: contar(nuevos.map((c) => c.origen || 'Otro')),
    porPrioridad: prios
      .map((p) => {
        const xs = nuevos.filter((c) => c.prioridad === p);
        const t = xs.map(minutosRespuesta).filter((x): x is number => x != null);
        return { prioridad: p, casos: xs.length, respuestaMediana: mediana(t), dentroSla: pct(t) };
      })
      .filter((x) => x.casos),
    porArea: contar(nuevos.map(areaDe)),
  };
}

/** Las areas (grupos de casos) que tienen movimiento: entraron, se resolvieron o siguen abiertos. */
export function areasDe(lista: Caso[], desde: Date, hasta: Date) {
  const en = (iso: string | null) => !!iso && Date.parse(iso) >= desde.getTime() && Date.parse(iso) < hasta.getTime();
  return contar(lista.filter((c) => en(c.creado) || en(c.cerradoEn) || !cerrado(c)).map(areaDe)).map(([a]) => a);
}
export const casosDelArea = (lista: Caso[], area: string | null) => (area ? lista.filter((c) => areaDe(c) === area) : lista);

/* ---------------- horas del equipo ---------------- */

export type FilaHorasPersona = { id: string | null; total: number; facturable: number; noFacturable: number; ocupacion: number | null };
export type DetalleHoras = {
  total: number;
  facturable: number;
  descontado: number;
  sinCobrar: number;
  sinCobrarLista: RegistroHoras[];
  porPersona: FilaHorasPersona[];
  porCliente: [string, number][];
  porProyecto: [string, number][];
};

/**
 * Horas registradas. "Sin cobrar": facturables, en un caso, que no se descontaron de ninguna bolsa (cliente sin
 * bolsa vigente). clienteDe/proyectoDe dan el nombre a mostrar de cada registro.
 */
export function detalleHoras(
  regs: RegistroHoras[],
  consumos: Pick<Movimiento, 'registroHorasId'>[],
  dias: number,
  clienteDe: (r: RegistroHoras) => string,
  proyectoDe: (r: RegistroHoras) => string,
): DetalleHoras {
  const descontados = new Set(consumos.map((m) => m.registroHorasId).filter(Boolean));
  const sinCobrarLista = regs.filter((r) => r.facturable && r.casoId && !descontados.has(r.id));
  const personas = new Map<string | null, FilaHorasPersona>();
  for (const r of regs) {
    const p = personas.get(r.miembroId) ?? { id: r.miembroId, total: 0, facturable: 0, noFacturable: 0, ocupacion: null };
    p.total += r.minutos;
    if (r.facturable) p.facturable += r.minutos;
    else p.noFacturable += r.minutos;
    personas.set(r.miembroId, p);
  }
  const porPersona = [...personas.values()].map((p) => ({ ...p, ocupacion: dias ? Math.round((100 * p.total) / (dias * 8 * 60)) : null })).sort((a, b) => b.total - a.total);
  return {
    total: regs.reduce((s, r) => s + r.minutos, 0),
    facturable: regs.filter((r) => r.facturable).reduce((s, r) => s + r.minutos, 0),
    descontado: regs.filter((r) => descontados.has(r.id)).reduce((s, r) => s + r.minutos, 0),
    sinCobrar: sinCobrarLista.reduce((s, r) => s + r.minutos, 0),
    sinCobrarLista,
    porPersona,
    porCliente: sumarMap(regs.map((r): [string, number] => [clienteDe(r), r.minutos])),
    porProyecto: sumarMap(regs.map((r): [string, number] => [proyectoDe(r), r.minutos])),
  };
}

/* ---------------- bolsas de soporte ---------------- */

export type BolsaR = Bolsa & { movs: Movimiento[]; saldo: Saldo };
export type DetalleBolsas = {
  vendidas: BolsaR[];
  /** Horas adicionales de Support 12 compradas en el periodo (se facturan aparte, US$70 c/u). */
  adicionales: (Movimiento & { bolsa: BolsaR })[];
  vendidoUsd: number;
  consumos: (Movimiento & { bolsa: BolsaR })[];
  consumido: number;
  trabajado: number;
  recargo: number;
  vencidoS12: number;
  paraRenovar: BolsaR[];
  renovarUsd: number;
  porCliente: [string, number][];
  porTecnico: [string, number][];
};

/**
 * Bolsas en el periodo: lo vendido (planes que empiezan en el periodo, para facturar), lo consumido (con lo que sumaron
 * el minimo de 20 min y los recargos), las horas de Support 12 que se vencieron sin usar y lo que hay que renovar hoy
 * (vence en 30 dias, vencio hace menos de 30 o se agoto).
 */
export function detalleBolsas(lista: BolsaR[], desde: Date, hasta: Date, ahora = Date.now()): DetalleBolsas {
  const en = (t: number) => t >= desde.getTime() && t < hasta.getTime();
  const vendidas = lista.filter((b) => b.plan !== 'ANTERIOR' && b.inicio && en(Date.parse(b.inicio)));
  const adicionales = lista.flatMap((b) => b.movs.filter((m) => esAdicional(m, b.plan) && en(Date.parse(m.fecha))).map((m) => ({ ...m, bolsa: b })));
  const consumos = lista.flatMap((b) => b.movs.filter((m) => m.tipo === 'CONSUMO' && en(Date.parse(m.fecha))).map((m) => ({ ...m, bolsa: b })));
  const consumido = consumos.reduce((s, m) => s + m.minutos, 0);
  const trabajado = consumos.reduce((s, m) => s + (m.minutosReales ?? m.minutos), 0);
  const vencidoS12 = lista.flatMap((b) => b.saldo.meses ?? []).filter((m) => en(m.hasta - 1)).reduce((s, m) => s + m.vencidos, 0);
  const ultimo = (b: BolsaR) => Math.max(0, ...b.movs.map((m) => Date.parse(m.fecha) || 0));
  const paraRenovar = lista
    .filter((b) => {
      const v = b.saldo.venceEn;
      if (v != null && v <= 30 && v >= -30) return true;
      return !b.saldo.vencida && b.plan !== 'SUPPORT_12' && b.saldo.restantes <= 0 && ahora - ultimo(b) <= 60 * DIA;
    })
    .sort((a, b) => (a.saldo.venceEn ?? 0) - (b.saldo.venceEn ?? 0));
  const precio = (b: BolsaR) => (b.plan === 'ANTERIOR' ? 0 : PLANES[b.plan].precio);
  return {
    vendidas,
    adicionales,
    vendidoUsd: vendidas.reduce((s, b) => s + precio(b), 0) + adicionales.reduce((s, m) => s + (m.minutos / 60) * HORA_ADICIONAL, 0),
    consumos,
    consumido,
    trabajado,
    recargo: consumido - trabajado,
    vencidoS12,
    paraRenovar,
    renovarUsd: paraRenovar.reduce((s, b) => s + (b.plan === 'ANTERIOR' ? PLANES.SUPPORT_6.precio : precio(b)), 0),
    porCliente: sumarMap(consumos.map((m): [string, number] => [m.bolsa.cliente, m.minutos])),
    porTecnico: sumarMap(consumos.map((m): [string, number] => [m.atendidoPor || 'Sin nombre', m.minutos])),
  };
}
